import React, { useCallback, useMemo, useState } from 'react'
import { Chat, textVariant } from '@/config'
import { alert } from '@codeleap/modals'
import { createStyles } from '@codeleap/styles'
import { ActionIcon, Text, View, ForwardMessageModal } from '@/components'
import { useChannelStateContext } from 'stream-chat-react'
import { PropsOf } from '@codeleap/types'

export type SelectionBarVariant = 'default' | 'delete' | 'forward'

export type SelectionBarProps = {
  onForward?: () => void
  variant?: SelectionBarVariant
  channelId?: string
  threadList?: boolean
}

type ActionIconProps = Pick<PropsOf<typeof ActionIcon>, 'name' | 'onPress' | 'style'>

export const ChatSelectModeBar = ({ threadList, ...props }: SelectionBarProps) => {
  const { selectMode } = Chat.Store.useSelectMode({ threadList })
  const selectedMessages = Chat.Store.useSelectedMessages()?.map(m => m?.id) || []
  const { channel } = useChannelStateContext()
  const [forwardModalVisible, setForwardModalVisible] = useState(false)

  const selectedMessagesCount = selectedMessages?.length ?? 0
  const actionsDisabled = selectedMessagesCount === 0

  const messages = channel?.state?.messages

  const handleDelete = useCallback(() => {
    Chat.Services.deleteMessages(selectedMessages)
    Chat.Store.setSelectMode(null)
  }, [selectedMessages, messages])

  const handleForward = useCallback(() => {
    setForwardModalVisible(true)
  }, [])

  const { actionProps } = useMemo((): {
    actionProps?: ActionIconProps
  } => {
    switch (selectMode) {
      case 'delete':
        return {
          actionProps: {
            name: 'trash-03',
            style: ['largeish', 'negativeSecondary'],
            onPress: () => {
              alert.ask({
                title: `Delete ${selectedMessagesCount} message${selectedMessagesCount > 1 ? 's' : ''}?`,
                options: [
                  { text: 'Cancel', style: 'cancel' },
                  { text: 'Confirm', style: 'destructive', onPress: handleDelete },
                ],
              })
            },
          },
        }
      case 'forward':
        return {
          actionProps: {
            name: 'share-06',
            style: ['largeish', 'regularSecondary'],
            onPress: handleForward,
          },
        }
    }
  }, [selectMode, handleDelete, handleForward])

  return (
    <>
      <View style={[styles.wrapper, threadList && styles.threadWrapper]}>
        <ActionIcon
          name='x'
          debugName='ChatSelectModeBar:LeftAction'
          style={['neutralSecondary', 'largeish']}
          onPress={Chat.Store.cancelSelectMode}
        />

        <Text text={`${selectedMessagesCount} Selected`} style={styles.text} />

        <View style={['marginLeft:auto']}>
          {!!actionProps && (
            <ActionIcon
              {...actionProps}
              debugName='ChatSelectModeBar:RightAction'
              disabled={actionsDisabled}
            />
          )}
        </View>
      </View>

      <ForwardMessageModal
        visible={forwardModalVisible}
        toggle={() => setForwardModalVisible(!forwardModalVisible)}
      />
    </>
  )
}

const styles = createStyles((theme) => ({
  wrapper: {
    ...theme.presets.row,
    ...theme.presets.fullWidth,
    ...theme.presets.alignCenter,
    backgroundColor: theme.colors.bgDefault,
    ...theme.spacing.padding(3),
    gap: theme.spacing.value(1),
  },
  threadWrapper: {
    marginTop: 'auto',
    ...theme.border({
      color: '#f6f6f6',
      directions: ['top'],
      width: 2,
    }),
  },
  text: {
    ...textVariant('p3'),
  },
}))
